import React from 'react';
import * as Sentry from '@sentry/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import i18n from '@/lib/i18n';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class PageErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Page error:', error, errorInfo);
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[400px] p-8">
          <Card className="max-w-md w-full">
            <CardContent className="flex flex-col items-center text-center pt-8 pb-6 space-y-4">
              <div className="h-14 w-14 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="h-7 w-7 text-destructive-ink" />
              </div>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold">{i18n.t('errorBoundary.title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18n.t('errorBoundary.message')}
                </p>
              </div>
              {this.state.error && (
                <p className="text-xs font-mono text-muted-foreground/60 bg-muted rounded-md px-3 py-2 max-w-full overflow-hidden text-ellipsis">
                  {this.state.error.message}
                </p>
              )}
              <Button onClick={this.handleReload} variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                {i18n.t('actions.tryAgain')}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
